import { BooleanChips, Chip } from "./types";

const EMPTY_CHIPS = (): BooleanChips => ({ and: [], or: [], not: [] });

/** Wraps a keyword in double quotes for the composed query. */
function quote(value: string): string {
	return `"${value}"`;
}

/**
 * Composes AND/OR/NOT chips into a single LinkedIn Boolean query string.
 *
 * Mirrors the mockup's `buildPlainQuery`:
 *   AND → `"a" AND "b"`
 *   OR  → `("c" OR "d")`, prefixed with `AND ` when AND terms exist
 *   NOT → `NOT "e" NOT "f"`
 * Parts are joined by spaces; an all-empty builder yields "".
 */
export function composeBooleanQuery(chips: BooleanChips): string {
	const ands = chips.and.map((c) => c.value.trim()).filter(Boolean);
	const ors = chips.or.map((c) => c.value.trim()).filter(Boolean);
	const nots = chips.not.map((c) => c.value.trim()).filter(Boolean);

	const parts: string[] = [];

	if (ands.length) {
		parts.push(ands.map(quote).join(" AND "));
	}

	if (ors.length) {
		const orGroup = `(${ors.map(quote).join(" OR ")})`;

		parts.push(parts.length ? `AND ${orGroup}` : orGroup);
	}

	if (nots.length) {
		parts.push(nots.map((value) => `NOT ${quote(value)}`).join(" "));
	}

	return parts.join(" ");
}

/** Result of attempting to parse a stored query back into builder chips. */
export interface ParseResult {
	chips: BooleanChips;
	/** True when the query maps cleanly onto the builder grammar (safe to edit as chips). */
	ok: boolean;
	/** The original, trimmed query string. */
	raw: string;
}

/** Collapses runs of whitespace and trims, for structural comparison. */
function normalizeWhitespace(value: string): string {
	return value.replace(/\s+/g, " ").trim();
}

/** Removes a single matched pair of surrounding double quotes, if present. */
function stripQuotes(token: string): string {
	const trimmed = token.trim();

	if (trimmed.length >= 2 && trimmed.startsWith('"') && trimmed.endsWith('"')) {
		return trimmed.slice(1, -1).trim();
	}

	return trimmed;
}

function toChips(values: string[]): Chip[] {
	return values.filter(Boolean).map((value) => ({ value }));
}

/**
 * Best-effort reverse of {@link composeBooleanQuery}.
 *
 * Extracts quoted NOT terms, a single parenthesized OR group, then the
 * remaining AND terms. `ok` is true only when recomposing the parsed chips
 * reproduces the original query verbatim (whitespace-normalized) — i.e. the
 * query was already in fully-quoted builder form. Free-form / legacy queries
 * return `ok: false` so callers can preserve the raw string unchanged.
 */
export function parseBooleanQuery(query: string | null | undefined): ParseResult {
	const raw = (query ?? "").trim();

	if (!raw) {
		return { chips: EMPTY_CHIPS(), ok: true, raw };
	}

	let working = raw;

	// 1. Quoted NOT terms: NOT "x"
	const nots: string[] = [];

	working = working.replace(/\bNOT\s+"([^"]*)"/g, (_match, term: string) => {
		nots.push(term.trim());

		return " ";
	});

	// 2. A single parenthesized OR group, with an optional preceding AND connector.
	const ors: string[] = [];

	working = working.replace(/(?:\bAND\s+)?\(([^()]*)\)/, (_match, inner: string) => {
		inner
			.split(/\bOR\b/)
			.map(stripQuotes)
			.filter(Boolean)
			.forEach((term) => ors.push(term));

		return " ";
	});

	// 3. Remaining AND terms.
	const ands = working
		.split(/\bAND\b/)
		.map(stripQuotes)
		.filter(Boolean);

	const chips: BooleanChips = {
		and: toChips(ands),
		or: toChips(ors),
		not: toChips(nots),
	};

	const ok = normalizeWhitespace(composeBooleanQuery(chips)) === normalizeWhitespace(raw);

	return { chips, ok, raw };
}
